import * as Phaser from 'phaser';
import type { Cell } from '../model/types';

export const cellSize = 50;
export const lineWidth = cellSize * 0.26;
export const cornerRadius = cellSize * 0.22;
export const cornerSegments = 6;
export const arrowHeadWidth = lineWidth * 2.6;
export const arrowHeadLength = lineWidth * 2.6;
export const arrowHeadOvershoot = arrowHeadLength * 0.55;
export const tailDotRadius = lineWidth * 0.55;
/*
 * 盤面の状態色。役割は「進行系＝ティール／ヒント＝琥珀／失敗＝赤」で固定し、
 * styles.css の --mint / --amber / --coral と同じ値をそろえる（ヘッダーの残り
 * カウンターの琥珀と、盤上のヒント矢印の琥珀が別色に見えないようにするため）。
 *
 * ヒントと失敗が「常時表示」になったので、盤面には最大で
 * オフホワイト・ティール・琥珀・赤の 4 系統が同時に並ぶ。うるささを抑えるため、
 * 出現数と重要度に合わせて明度を段階化してある（枠内背景 #103a5a 比）:
 *
 *   lineColor  10.4 : 未操作。数は多いが無彩色なので彩度の競合には加わらない
 *   hintColor   7.0 : 広告 1 回につき 1 本だけ。最も明るい有彩色＝いちばん目立つ
 *   checkedColor 6.1 : 自分で付ける印。盤面の大半に付き得るので彩度を落として鎮める
 *   invalidColor 4.2 : 失敗。同時に 2 本まで。暗い赤にして警告色として沈ませる
 *
 * ヒントと失敗の明度比を 1.68 まで開いてあるのは、琥珀と赤が色相で 30° ほどしか
 * 離れておらず、P/D 型色覚では色相だけでは分離できないため。
 */
export const lineColor = 0xe6f3f1;
export const hoverColor = 0xa6f3e4;
export const checkedColor = 0x60cdb5;
export const hintColor = 0xefbf6c;
export const invalidColor = 0xec7669;
export const guideColor = 0x60cdb5;
/*
 * 盤面の下地＝レーン格子。セル境界にだけ引く細線で、矢印が進むレーンと
 * 衝突相手の位置を目で追うための唯一の方眼（シェル側の背景方眼は、セルと
 * 無関係な升目が偽のレーンに見えるため廃した）。
 *
 * 難易度色へ寄せるのはこの下地だけで、矢印の状態色（白・ティール・琥珀・赤）と
 * 移動中の識別色には一切触れない。混合率を 0.55 に留めているのは、下地が
 * 有彩色に寄りすぎると赤（失敗）と琥珀（ヒント）の背景コントラストが
 * 難易度ごとに揺れてしまうため。
 *
 * 線は焼き込み層の 1 テクセル幅（= 実機の 1 物理ピクセル）で描き、層全体を
 * boardLatticeAlpha で薄める。線ごとに半透明を掛けないのは、交点で不透明度が
 * 二重になり、格子の交差点だけ玉のように濃く出てしまうため。
 */
export const boardLatticeColor = 0xb8d7d7;
export const boardLatticeAlpha = 0.17;
export const boardLatticeGradeMix = 0.55;
/**
 * レーン線の太さ（焼き込み層のテクセル数）。焼き込み解像度はカメラ倍率に
 * 追従するので、テクセルで定義しておけば拡大率によらず画面上の太さが揃う。
 * 1.5 テクセルにしてあるのは、ちょうど 1 テクセルだと配置の端数でアンチ
 * エイリアスが 2 テクセルへ割れ、線が薄く霞むことがあるため。
 */
export const boardLatticeTexelWidth = 1.5;

// --- 離陸（成功時の退出）演出のチューニング定数 -------------------------------
/**
 * 退出イージングの初速（平均速度に対する倍率）。タップ直後からこの速度で
 * 滑り出す。失敗演出（65〜180ms の短い衝突）に初速で見劣りしないための線形項。
 */
export const exitEaseInitialShare = 0.55;
/** 退出所要時間 = 退出距離 / この係数（下限・上限でクランプ）。 */
export const exitDurationDivisor = 1.75;
export const exitDurationMin = 250;
export const exitDurationMax = 480;
/** 退出イージング終端の速度（平均速度に対する倍率）。 */
export const exitEasePeakSlope = exitEaseInitialShare + (1 - exitEaseInitialShare) * 4;
/** 進捗 u から体感速度を近似する指数（launchEase の加速形状に合わせた近似）。 */
export const exitIntensityExponent = 0.75;
/**
 * 体感速度の下限。launchEase は線形項ぶんの初速を持つので、u=0 でも
 * 終端速度の initialShare/peakSlope だけ出ている。これを無視すると
 * 動き出しているのに航跡が無い 1〜2 フレームができる。
 */
export const exitIntensityFloor = exitEaseInitialShare / exitEasePeakSlope;
/** 航跡の長さ＝直近この時間(ms)に進んだ距離。 */
export const wakeTrailDuration = 38;
/** 航跡の最大長。 */
export const wakeMaxSpan = cellSize * 2.8;
export const wakeDepth = 9;
/** 飛び去ったあと航跡が消えるまでの余韻。 */
export const wakeFadeDuration = 260;
/** 尾端が盤外へ出てから消えるまでの距離（この区間でアルファを落とす）。 */
export const exitFadeSpan = cellSize;
/**
 * 移動中の識別色。以前は加速に応じて lineColor 側へ寄せていたが、それだと
 * 長い矢印ほど静止中のオフホワイト矢印に紛れて「どれが動いているか」が
 * 分からなくなる。ティールに固定することで、濃紺背景に対して約 6.5:1、
 * 静止矢印（lineColor）に対しても輝度比 1.7 + 明確な色相差を確保する。
 */
export const exitFlightColor = checkedColor;
/**
 * 加速に応じて識別色を hoverColor（明るいティール）側へ寄せる強さ。
 * ティールの範囲内でだけ動かすので、速度が乗っても白側へは抜けない。
 *
 * 本体まわりに半透明の太ストロークを重ねる「光暈」も試したが、Phaser の
 * Graphics は丸ジョイントを持たないため曲がり角で幅に比例した欠けが出て、
 * ピンチズーム時に斜めの切れ目として露見する。ブループリントの線の潔さを
 * 優先し、発光は芯の色味と航跡側のにじみだけで表現する。
 */
export const exitSpeedTint = 0.45;
/**
 * 発進の瞬間だけ芯を純白へ振る。パレットで最も明るい lineColor でも
 * 静止中の矢印と同色なので「光った」と読めず、白まで抜いて初めて鋭さが出る。
 * 3 乗で落とすので 60fps では実質 1 フレームの閃光。短くしているのは、
 * 白い間だけは静止矢印と同系色になり識別色（下記）が効かないため。
 */
export const exitFlashColor = 0xffffff;
export const exitFlashDuration = 45;
/**
 * レーダーピング。タップ地点から細いリングが 1 本広がって消える。
 * 航空図の走査線と同じ語彙なので、線は細く・アルファは低く・色は既存の
 * hoverColor のままに留める。
 *
 * 半径と線幅は「画面ピクセル」で定義し、毎フレーム現在のカメラズームで割って
 * ワールド単位へ直す（ピング中のピンチズームにも追従する）。盤面の大きさで
 * ズームが 0.45〜1.3 倍まで変わるため、ワールド固定にすると大盤面で 1px 未満の
 * 細さになり、指の下の手応えが盤面依存になってしまう。
 */
export const pingDepth = 8;
export const pingDuration = 320;
export const pingStartRadius = 11;
export const pingEndRadius = 64;
export const pingLineWidth = 2.2;
export const pingAlpha = 0.62;
/**
 * 航跡は「尾端から spanRatio 分だけ遡る帯」を重ねて弧長方向に減衰させる。
 * 重なりで実効アルファは尾端側から約 0.60 → 0.09 へ、幅も本体幅から 1/5 へ
 * 段階的に細り、飛行機雲のように尖って消える。段数を細かく刻んでいるのは、
 * 各パスの平らな端キャップが曲がり角に落ちると斜めの段差として見えるため。
 */
export const wakePasses: ReadonlyArray<{
  readonly spanRatio: number;
  readonly widthRatio: number;
  readonly alpha: number;
  readonly color: number;
}> = [
  { spanRatio: 1, widthRatio: 0.2, alpha: 0.09, color: checkedColor },
  { spanRatio: 0.66, widthRatio: 0.33, alpha: 0.11, color: checkedColor },
  { spanRatio: 0.44, widthRatio: 0.47, alpha: 0.13, color: hoverColor },
  { spanRatio: 0.29, widthRatio: 0.62, alpha: 0.15, color: hoverColor },
  { spanRatio: 0.19, widthRatio: 0.78, alpha: 0.17, color: hoverColor },
  { spanRatio: 0.12, widthRatio: 0.94, alpha: 0.19, color: hoverColor },
];

/** 初速を持って滑り出し、盤外へ一気に加速する退出プロファイル。 */
export function launchEase(time: number): number {
  const squared = time * time;
  return exitEaseInitialShare * time + (1 - exitEaseInitialShare) * squared * squared;
}

export function mixColor(from: number, to: number, ratio: number): number {
  const amount = ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
  const red = (from >> 16 & 0xff) + ((to >> 16 & 0xff) - (from >> 16 & 0xff)) * amount;
  const green = (from >> 8 & 0xff) + ((to >> 8 & 0xff) - (from >> 8 & 0xff)) * amount;
  const blue = (from & 0xff) + ((to & 0xff) - (from & 0xff)) * amount;
  return Math.round(red) << 16 | Math.round(green) << 8 | Math.round(blue);
}
const unitOctagon: readonly Phaser.Math.Vector2[] = Array.from({ length: 8 }, (_, index) => {
  const angle = Math.PI * 2 * index / 8;
  return new Phaser.Math.Vector2(Math.cos(angle), Math.sin(angle));
});
const octagonScratch: Phaser.Math.Vector2[] = Array.from(
  { length: 8 },
  () => new Phaser.Math.Vector2(),
);

export function octagonAt(center: Cell, radius: number): Phaser.Math.Vector2[] {
  for (let index = 0; index < unitOctagon.length; index += 1) {
    const unitPoint = unitOctagon[index];
    const target = octagonScratch[index];
    if (unitPoint !== undefined && target !== undefined) {
      target.set(center.x + unitPoint.x * radius, center.y + unitPoint.y * radius);
    }
  }
  return octagonScratch;
}
